import { Box, Chip, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getStatusStyles, CLIENT_STATUS } from '../../clients.styles'
import { getStatusLabel, getStatusTooltip } from '../../clients.constants'
import { styles } from './retention-badges.styles'

interface RetentionBadgesProps {
  membershipStatus?: string | null
  /** OC-100: active block reason, shown in the Blocked pill tooltip (staff-only). */
  blockReason?: string | null
  daysAbsent?: number | null
}

interface BadgeConfig {
  labelKey: string
  bgcolor: string
  color: string
}

export const DAYS_ABSENT_THRESHOLDS: {
  days: number
  labelKey: string
  bgcolor: string
  color: string
}[] = [
  {
    days: 60,
    labelKey: 'clients.retentionBadges.daysAbsent',
    bgcolor: 'error.lightest',
    color: 'error.dark',
  },
  {
    days: 30,
    labelKey: 'clients.retentionBadges.daysAbsent',
    bgcolor: 'orange.lightest',
    color: 'orange.dark',
  },
  {
    days: 14,
    labelKey: 'clients.retentionBadges.daysAbsent',
    bgcolor: 'warning.lightest',
    color: 'warning.dark',
  },
  {
    days: 7,
    labelKey: 'clients.retentionBadges.daysAbsent',
    bgcolor: 'yellow.lightest',
    color: 'yellow.dark',
  },
  {
    days: 0,
    labelKey: 'clients.retentionBadges.active',
    bgcolor: 'success.lightest',
    color: 'success.dark',
  },
]

export function getDaysAbsentBadge(daysAbsent: number | null | undefined): BadgeConfig | null {
  if (daysAbsent == null) {
    return null
  }
  return DAYS_ABSENT_THRESHOLDS.find((threshold) => daysAbsent >= threshold.days) ?? null
}

const RetentionBadges = ({ membershipStatus, blockReason }: RetentionBadgesProps) => {
  const { t } = useTranslation('core')

  const statusStyles = membershipStatus ? getStatusStyles(membershipStatus) : null
  const statusLabel = membershipStatus ? getStatusLabel(membershipStatus, t) : null
  const isBlocked = membershipStatus === CLIENT_STATUS.BLOCKED
  // A blocked client's pill surfaces the reason to staff; other statuses keep their generic tooltip.
  const statusTooltip =
    isBlocked && blockReason
      ? t('clients.block.reasonTooltip', { reason: blockReason })
      : membershipStatus
        ? getStatusTooltip(membershipStatus, t)
        : ''
  const showStatus = statusStyles && statusLabel

  // OC-782: the days-absent chip is hidden for now — it counts class attendance only, which
  // misreads as "absent" for members who attend via general check-in. Kept commented (not
  // deleted) so it can be restored once the metric accounts for general check-ins: re-add the
  // `daysAbsent` prop to the signature and the `daysAbsentBadge` line below, and uncomment the chip.
  // const daysAbsentBadge = getDaysAbsentBadge(daysAbsent)
  if (!showStatus) {
    return null
  }

  return (
    <Box sx={styles.badgeRow}>
      {showStatus && (
        <Tooltip title={statusTooltip} arrow>
          <Chip
            label={statusLabel}
            size="small"
            sx={styles.chip(statusStyles.bgcolor, statusStyles.color)}
          />
        </Tooltip>
      )}
      {/* OC-782: days-absent chip hidden for now (see note above).
      {daysAbsentBadge && daysAbsent != null && (
        <Tooltip
          title={t('clients.retentionBadges.tooltipDaysAbsent', { count: daysAbsent })}
          arrow
        >
          <Chip
            label={t(daysAbsentBadge.labelKey, { count: daysAbsent })}
            size="small"
            sx={styles.chip(daysAbsentBadge.bgcolor, daysAbsentBadge.color)}
          />
        </Tooltip>
      )} */}
    </Box>
  )
}

export default RetentionBadges
